import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { PaginatedResponse } from '@/shared/types/api.types';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';

import type {
  CreateCustomerPayload,
  Customer,
  CustomerExportParams,
  CustomerExportResult,
  CustomerFilters,
  CustomerImportParams,
  CustomerImportResult,
  CustomerListParams,
  CustomerSearchParams,
  DeleteCustomerByCodeParams,
  GetCustomerByIdParams,
  Pro20Page,
  UpdateCustomerPayload,
} from '../types/customer.types';
import {
  buildCustomerFiltersCacheKey,
  DEFAULT_CUSTOMER_FILTERS,
  applyCustomerFiltersToSearchParams,
} from '../utils/customer-filter-params';
import { normalizeCustomersResponse } from '../utils/normalize-customers';

function resolveFilters(filters?: CustomerFilters): CustomerFilters {
  return filters ?? DEFAULT_CUSTOMER_FILTERS;
}

function appendFilterParams(
  params: URLSearchParams,
  filters: CustomerFilters,
): void {
  applyCustomerFiltersToSearchParams(params, filters);
}

function buildCustomersUrl({
  branchCode,
  page = 0,
  size = 20,
  search,
  filters,
}: CustomerListParams): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));

  if (search?.trim()) {
    params.set('search', search.trim());
  }

  appendFilterParams(params, resolveFilters(filters));

  return `/branches/${branchCode}/customers?${params.toString()}`;
}

function filtersChanged(
  current?: CustomerFilters,
  previous?: CustomerFilters,
): boolean {
  return (
    buildCustomerFiltersCacheKey(resolveFilters(current)) !==
    buildCustomerFiltersCacheKey(resolveFilters(previous))
  );
}

export const customersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    customerMarketingStatus: builder.query<
      { marketingStatus: string | null; updatedAt?: string },
      { branch: string; customerId: number }
    >({
      query: ({ branch, customerId }) =>
        `/branches/${branch}/customers/${String(customerId)}/marketing-status`,
      providesTags: (_r, _e, { branch }) => [
        { type: API_TAGS.Customers, id: branch },
      ],
    }),
    updateCustomerMarketingStatus: builder.mutation<
      { marketingStatus: string | null },
      { branch: string; customerId: number; marketingStatus: string | null }
    >({
      query: ({ branch, customerId, marketingStatus }) => ({
        url: `/branches/${branch}/customers/${String(customerId)}/marketing-status`,
        method: 'PUT',
        body: { marketingStatus },
      }),
      invalidatesTags: (_r, _e, { branch }) => [
        { type: API_TAGS.Customers, id: branch },
      ],
    }),
    getCustomers: builder.query<
      PaginatedResponse<Customer>,
      CustomerListParams
    >({
      query: (args) => ({
        url: buildCustomersUrl(args),
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          branchCode,
          page = 0,
          size = 20,
          search = '',
          filtersKey,
        } = queryArgs;
        const resolvedFiltersKey =
          filtersKey ??
          buildCustomerFiltersCacheKey(resolveFilters(queryArgs.filters));
        return `${endpointName}(${branchCode}|${String(page)}|${String(size)}|${search}|${resolvedFiltersKey})`;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!previousArg || !currentArg) {
          return true;
        }
        return (
          currentArg.search !== previousArg.search ||
          currentArg.page !== previousArg.page ||
          currentArg.size !== previousArg.size ||
          currentArg.branchCode !== previousArg.branchCode ||
          filtersChanged(currentArg.filters, previousArg.filters)
        );
      },
      transformResponse: (
        response:
          | Pro20ApiResponse<Pro20Page<Customer> | Customer[]>
          | Pro20Page<Customer>
          | Customer[],
        _meta,
        arg,
      ) => {
        const results = normalizePro20Response(response);
        return normalizeCustomersResponse(results, arg.size ?? 20);
      },
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Customers, id: branchCode },
      ],
    }),
    updateCustomer: builder.mutation<Customer, UpdateCustomerPayload>({
      query: (body) => ({
        url: '/customer/updatecustomer',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as Customer,
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Customers, id: branchCode },
      ],
    }),
    createCustomer: builder.mutation<Customer, CreateCustomerPayload>({
      query: (body) => ({
        url: '/customer/save',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as Customer,
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Customers, id: branchCode },
      ],
    }),
    exportCustomers: builder.query<CustomerExportResult, CustomerExportParams>({
      query: ({
        branchCode,
        search,
        sortBy = 'lastName',
        sortDirection = 'asc',
        filters,
      }) => {
        const params = new URLSearchParams();

        if (search?.trim()) {
          params.set('search', search.trim());
        }

        params.set('sortBy', sortBy);
        params.set('sortDirection', sortDirection);
        appendFilterParams(params, resolveFilters(filters));

        return {
          url: `/branches/${encodeURIComponent(branchCode)}/customers/export?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as CustomerExportResult,
    }),
    getCustomerImportStatus: builder.query<
      CustomerImportResult,
      { branchCode: string; importId: string }
    >({
      query: ({ branchCode, importId }) =>
        `/customer/importcustomers/${encodeURIComponent(branchCode)}/${encodeURIComponent(importId)}`,
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as CustomerImportResult,
    }),
    importCustomers: builder.mutation<
      CustomerImportResult,
      CustomerImportParams
    >({
      query: ({ branchCode, file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `/customer/importcustomers/${encodeURIComponent(branchCode)}`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as CustomerImportResult,
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Customers, id: branchCode },
      ],
    }),
    getCustomerById: builder.query<Customer, GetCustomerByIdParams>({
      query: ({ branchCode, customerId }) => ({
        url: `/branches/${encodeURIComponent(branchCode)}/customers/${encodeURIComponent(String(customerId))}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<Customer> | Customer) =>
        normalizePro20Response(response),
      providesTags: (_result, _error, { branchCode, customerId }) => [
        { type: API_TAGS.Customers, id: `${branchCode}-${String(customerId)}` },
      ],
    }),
    searchCustomersByQuery: builder.query<Customer[], CustomerSearchParams>({
      query: ({ branchCode, query, page = 0, size = 20 }) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('size', String(size));
        params.set('query', query.trim());

        return {
          url: `/customer/getcustomers/${encodeURIComponent(branchCode)}?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (
        response:
          | Pro20ApiResponse<Pro20Page<Customer> | Customer[]>
          | Pro20Page<Customer>
          | Customer[],
      ) => {
        const results = normalizePro20Response(response);

        if (Array.isArray(results)) {
          return results;
        }

        if (typeof results === 'object' && Array.isArray(results.content)) {
          return results.content;
        }

        return [];
      },
    }),
    deleteCustomerByCode: builder.mutation<null, DeleteCustomerByCodeParams>({
      query: ({ customerCode }) => ({
        url: `/customer/deletebycode/${encodeURIComponent(customerCode)}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
      invalidatesTags: (_result, _error, { branchCode }) =>
        branchCode
          ? [{ type: API_TAGS.Customers, id: branchCode }]
          : [{ type: API_TAGS.Customers, id: 'LIST' }],
    }),
  }),
});

export const {
  useCustomerMarketingStatusQuery,
  useUpdateCustomerMarketingStatusMutation,
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useUpdateCustomerMutation,
  useCreateCustomerMutation,
  useLazyExportCustomersQuery,
  useImportCustomersMutation,
  useGetCustomerImportStatusQuery,
  useGetCustomerByIdQuery,
  useSearchCustomersByQueryQuery,
  useDeleteCustomerByCodeMutation,
} = customersApi;
